import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface CRQWKVisualizationProps {
  qwkData: Record<string, any>;
}

const CRQWKVisualization: React.FC<CRQWKVisualizationProps> = ({ qwkData }) => {
  const getQWKColor = (qwk: number): string => {
    if (qwk >= 0.8) return '#4caf50'; // Green - Excellent
    if (qwk >= 0.6) return '#ff9800'; // Orange - Good
    return '#f44336'; // Red - Needs improvement
  };

  const getQWKLabel = (qwk: number): string => {
    if (qwk >= 0.8) return 'Excellent';
    if (qwk >= 0.6) return 'Good';
    if (qwk >= 0.4) return 'Moderate';
    if (qwk >= 0.2) return 'Fair';
    return 'Poor';
  };

  const formatNumber = (num: number | undefined, decimals: number = 4): string => {
    return num !== undefined ? num.toFixed(decimals) : 'N/A';
  };

  const overall = qwkData.overall || {};
  const gradeBreakdown = qwkData.grade_breakdown || {};
  const characteristicAnalysis = qwkData.characteristic_analysis || {};

  if (!overall.qwk_calculated) {
    return (
      <Alert severity="info">
        QWK analysis not available. This may be because the dataset does not include teacher/human scores for comparison.
      </Alert>
    );
  }

  // Prepare data for grade breakdown chart
  const gradeChartData = Object.entries(gradeBreakdown).map(([grade, metrics]: [string, any]) => ({
    grade: grade,
    qwk: metrics.qwk || 0,
    count: metrics.total_pairs || 0
  })).sort((a, b) => {
    // Sort grades numerically
    const gradeA = parseInt(a.grade.replace(/\D/g, '')) || 0;
    const gradeB = parseInt(b.grade.replace(/\D/g, '')) || 0;
    return gradeA - gradeB;
  });

  return (
    <Box>
      {/* Overall Metrics Cards */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Overall Agreement Metrics
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                QWK (Quadratic Weighted Kappa)
              </Typography>
              <Typography variant="h4" sx={{ color: getQWKColor(overall.qwk || 0) }}>
                {formatNumber(overall.qwk, 3)}
              </Typography>
              <Chip
                label={getQWKLabel(overall.qwk || 0)}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: getQWKColor(overall.qwk || 0),
                  color: 'white'
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Cohen's Kappa
              </Typography>
              <Typography variant="h4">
                {formatNumber(overall.cohen_kappa, 3)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Unweighted agreement
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Mean Absolute Error
              </Typography>
              <Typography variant="h4">
                {formatNumber(overall.mae, 2)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Average score difference
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Correlation
              </Typography>
              <Typography variant="h4">
                {formatNumber(overall.correlation, 3)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Pearson correlation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Exact Match Rate
              </Typography>
              <Typography variant="h4">
                {formatNumber((overall.exact_match_rate || 0) * 100, 1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {overall.exact_matches || 0} of {overall.total_pairs || 0} pairs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Teacher Mean Score
              </Typography>
              <Typography variant="h4">
                {formatNumber(overall.teacher_mean, 2)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                SD: {formatNumber(overall.teacher_std, 2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                LLM Mean Score
              </Typography>
              <Typography variant="h4">
                {formatNumber(overall.llm_mean, 2)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                SD: {formatNumber(overall.llm_std, 2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grade-Level Breakdown */}
      {gradeChartData.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            QWK by Grade Level
          </Typography>

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gradeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade" />
                  <YAxis domain={[0, 1]} />
                  <Tooltip
                    formatter={(value: any, name: string) => {
                      if (name === 'qwk') return [value.toFixed(3), 'QWK'];
                      if (name === 'count') return [value, 'Sample Size'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="qwk" name="QWK Score">
                    {gradeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getQWKColor(entry.qwk)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Grade</strong></TableCell>
                      <TableCell align="right"><strong>QWK</strong></TableCell>
                      <TableCell align="right"><strong>Sample Size</strong></TableCell>
                      <TableCell align="right"><strong>Exact Match %</strong></TableCell>
                      <TableCell align="right"><strong>MAE</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gradeChartData.map((row) => {
                      const metrics = gradeBreakdown[row.grade];
                      return (
                        <TableRow key={row.grade}>
                          <TableCell>{row.grade}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatNumber(row.qwk, 3)}
                              size="small"
                              sx={{
                                bgcolor: getQWKColor(row.qwk),
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell align="right">
                            {formatNumber((metrics.exact_match_rate || 0) * 100, 1)}%
                          </TableCell>
                          <TableCell align="right">{formatNumber(metrics.mae, 2)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Characteristic Analysis */}
      {Object.keys(characteristicAnalysis).length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            QWK by Student Characteristics
          </Typography>

          <Grid container spacing={2}>
            {Object.entries(characteristicAnalysis).map(([characteristic, groups]: [string, any]) => (
              <Grid item xs={12} md={6} key={characteristic}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {characteristic}
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Group</strong></TableCell>
                            <TableCell align="right"><strong>QWK</strong></TableCell>
                            <TableCell align="right"><strong>Count</strong></TableCell>
                            <TableCell align="right"><strong>LLM Mean</strong></TableCell>
                            <TableCell align="right"><strong>Teacher Mean</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(groups).map(([group, metrics]: [string, any]) => (
                            <TableRow key={group}>
                              <TableCell>{group}</TableCell>
                              <TableCell align="right">
                                {metrics.qwk_calculated ? (
                                  <Chip
                                    label={formatNumber(metrics.qwk, 3)}
                                    size="small"
                                    sx={{
                                      bgcolor: getQWKColor(metrics.qwk),
                                      color: 'white'
                                    }}
                                  />
                                ) : (
                                  'N/A'
                                )}
                              </TableCell>
                              <TableCell align="right">{metrics.total_pairs || 0}</TableCell>
                              <TableCell align="right">{formatNumber(metrics.llm_mean, 2)}</TableCell>
                              <TableCell align="right">{formatNumber(metrics.teacher_mean, 2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Data Sanitization Summary */}
      {overall.sanitization_stats && (
        <>
          <Divider sx={{ my: 4 }} />
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Sanitization Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Original Rows
                  </Typography>
                  <Typography variant="h6">
                    {overall.sanitization_stats.original_rows || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Final Rows
                  </Typography>
                  <Typography variant="h6">
                    {overall.sanitization_stats.final_rows || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Removed Rows
                  </Typography>
                  <Typography variant="h6">
                    {overall.sanitization_stats.removed_rows || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Retention Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(overall.sanitization_stats.retention_rate_pct, 1)}%
                  </Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Data sanitization removes low-variance teachers (those who assign the same score consistently)
                to improve the reliability of agreement metrics.
              </Alert>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default CRQWKVisualization;
